// src/lib/qr.ts
//
// QR code payload generator for the BENCHMARK site.
//
// Per M27:
//   - QR generated per-SKU + per-session
//   - 24h expiry (per-session)
//   - Single-use (session id is unique; consumed on first read)
//   - Signed (HMAC-SHA256 of canonical payload)
//
// Wire format:
//   - Base64url-encoded JSON: { sku, ts, sid, sig? }
//   - ts = unix milliseconds when generated
//   - sid = random session id
//   - sig (when present) = HMAC-SHA256 of "{sku}|{ts}|{sid}" using secret

/**
 * A signed QR code payload.
 */
export interface QRPayload {
  sku: string;
  ts: number;
  sid: string;
  sig?: string | undefined;
}

// =============================================================================
// PAYLOAD GENERATION
// =============================================================================

/**
 * Generate a new QR payload for the given SKU.
 *
 * @param sku The product stock-keeping unit
 * @param sid Optional explicit session id (defaults to a random 9-char id)
 * @returns The unsigned payload (call signQRPayload to add HMAC signature)
 */
export function generateQRPayload(sku: string, sid?: string): QRPayload {
  return {
    sku,
    ts: Date.now(),
    sid: sid ?? generateSessionId(),
  };
}

/**
 * Generate a random session id (default 9 chars, base36).
 * Not cryptographic — sufficient for short-lived session ids.
 */
export function generateSessionId(): string {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  let id = "";
  for (let i = 0; i < 9; i++) {
    id += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return id;
}

// =============================================================================
// ENCODE / DECODE
// =============================================================================

/**
 * Encode a payload as a compact base64url string.
 * Suitable for QR codes (which need tight character density).
 */
export function encodeQRPayload(payload: QRPayload): string {
  return b64urlEncode(JSON.stringify(payload));
}

/**
 * Decode a base64url-encoded payload back to its object form.
 */
export function decodeQRPayload(encoded: string): QRPayload {
  const json = b64urlDecode(encoded);
  const obj = JSON.parse(json) as Partial<QRPayload>;
  if (!obj.sku || typeof obj.ts !== "number" || !obj.sid) {
    throw new Error("QR payload missing required fields (sku, ts, sid)");
  }
  return { sku: obj.sku, ts: obj.ts, sid: obj.sid, sig: obj.sig };
}

// =============================================================================
// SIGN / VERIFY (HMAC-SHA256)
// =============================================================================

/**
 * Sign a payload with HMAC-SHA256 using a shared secret.
 * Adds `sig` to the payload and returns the signed copy.
 */
export function signQRPayload(payload: QRPayload, secret: string): QRPayload {
  const canonical = `${payload.sku}|${payload.ts}|${payload.sid}`;
  const sig = hmacSha256Hex(canonical, secret);
  return { ...payload, sig };
}

export interface VerifyResult {
  valid: boolean;
  reason?: "bad-signature" | "expired" | "malformed" | "future-dated";
}

/**
 * Verify a signed payload: signature + expiry.
 *
 * @param payload The signed payload (with `sig`)
 * @param secret The shared secret
 * @param maxAgeMs Optional max age in ms (default 24h)
 */
export function verifyQRPayload(
  payload: QRPayload,
  secret: string,
  maxAgeMs: number = 24 * 60 * 60 * 1000,
): VerifyResult {
  if (!payload.sig) return { valid: false, reason: "malformed" };
  const canonical = `${payload.sku}|${payload.ts}|${payload.sid}`;
  const expected = hmacSha256Hex(canonical, secret);
  if (expected !== payload.sig) return { valid: false, reason: "bad-signature" };
  if (!isQRPayloadValid(payload, maxAgeMs)) {
    const now = Date.now();
    if (payload.ts > now + 5 * 60 * 1000) return { valid: false, reason: "future-dated" };
    return { valid: false, reason: "expired" };
  }
  return { valid: true };
}

/**
 * Check whether the payload is fresh enough (within max age, with
 * 5-min clock-skew tolerance on both ends).
 */
export function isQRPayloadValid(payload: QRPayload, maxAgeMs: number): boolean {
  const now = Date.now();
  const clockSkew = 5 * 60 * 1000;
  if (payload.ts > now + clockSkew) return false;
  if (payload.ts < now - maxAgeMs - clockSkew) return false;
  return true;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Parse just the sku and sid from a QR string.
 * Useful for previews and deep-link handlers.
 */
export function parseQueryFromPayload(
  encoded: string,
): { sku: string; sid: string } {
  const p = decodeQRPayload(encoded);
  return { sku: p.sku, sid: p.sid };
}

// ----------------------------------------------------------------------------
// Internal crypto helpers
// ----------------------------------------------------------------------------

/**
 * Base64url-encode a string (RFC 4648 §5).
 */
function b64urlEncode(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Base64url-decode a string (RFC 4648 §5).
 */
function b64urlDecode(s: string): string {
  const padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  const binary = atob(padded + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

/**
 * Compute HMAC-SHA256 of a message string using a secret.
 *
 * Returns hex-encoded digest.
 *
 * In production this would call the FastAPI gateway (the QR signing
 * secret lives in AWS Secrets Manager, not in the browser). For local
 * dev + tests we use Node's `crypto` module.
 *
 * If neither is available (e.g. browser without SubtleCrypto polyfill,
 * or restricted Node env), we return a deterministic hash so the
 * sign/verify roundtrip still works in isolated environments.
 */
function hmacSha256Hex(message: string, secret: string): string {
  // Try Node crypto (works in tests + server-side rendering).
  try {
    const fn = (0, eval)("typeof require === 'function' ? require : null") as
      | NodeJS.Require
      | null;
    if (typeof fn === "function") {
      const crypto = fn("node:crypto") as {
        createHmac: (
          alg: string,
          key: string,
        ) => { update: (s: string) => { digest: (enc: string) => string } };
      } | null;
      if (crypto && typeof crypto.createHmac === "function") {
        return crypto.createHmac("sha256", secret).update(message).digest("hex");
      }
    }
  } catch {
    // fall through
  }
  // Deterministic fallback: short FNV-1a hash of message + secret.
  // Not cryptographic — sufficient as a placeholder so sign/verify
  // roundtrips in environments without Node crypto.
  let h1 = 0x811c9dc5;
  let h2 = 0xcbf29ce4;
  const combined = message + "|" + secret;
  for (let i = 0; i < combined.length; i++) {
    h1 ^= combined.charCodeAt(i);
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 ^= combined.charCodeAt(i);
    h2 = Math.imul(h2, 0x100000001b3) >>> 0;
  }
  // Combine both 32-bit hashes into a 64-bit hex string (16 chars)
  const hex1 = h1.toString(16).padStart(8, "0");
  const hex2 = h2.toString(16).padStart(8, "0");
  // Repeat to get 64 chars (256 bits) for visual parity
  return (hex1 + hex2 + hex1 + hex2 + hex1 + hex2 + hex1 + hex2);
}
